import React from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from '@apollo/client';
import { get } from 'lodash';
import CCEvents, { EventNames } from '@companycam/companycam-events';
import { injectIntl, defineMessages } from 'react-intl';

import toast from 'helpers/toast';
import { graphQLIdGenerator } from 'helpers/graphql-utils';
import { ActionLink } from 'components/shared';
import CreateForm from './CreateForm';
import {
  GET_PHOTO_PAGE_LAYOUTS,
  CREATE_REPORT,
  CREATE_REPORT_NODE_BULK,
  CREATE_REPORT_NODE,
} from './gql/reports.graphql';
import { NEW_SECTION_DEFAULTS } from './constants';
import useShowReportTemplateTooltip from 'components/projects/taskList/hooks/useShowReportTemplateTooltip';

const messages = defineMessages({
  modalTitle: {
    id: 'Reports.create.modalTitle',
    defaultMessage: 'Create New Report',
    description: 'Modal title for creating a new report',
  },
  titleRequired: {
    id: 'Reports.create.titleRequired',
    defaultMessage: 'Report title required',
    description: 'Report create title validation message',
  },
  layoutRequired: {
    id: 'Reports.create.layoutRequired',
    defaultMessage: 'Please select a photo page layout',
    description: 'Report create layout validation message',
  },
  createError: {
    id: 'Reports.create.error',
    defaultMessage: 'There was a problem creating your report',
    description: 'Create report error message',
  },
  addItemError: {
    id: 'Reports.entries.createError',
    defaultMessage: 'There was a problem adding item to report',
    description: 'Add item to report error message',
  },
  sectionCreateError: {
    id: 'Reports.sections.createError',
    defaultMessage: 'There was a problem creating the section',
    description: 'Create report section error message',
  },
});

const getLayouts = (data) => {
  return get(data, 'reportNodeLayouts.nodes', []);
};

const databaseIdFrom = (obj) => parseInt(obj.databaseId, 10);
const imageNodeForId = (imageId) => {
  return graphQLIdGenerator('Image', imageId);
};

const CreateReport = ({
  isClosable,
  onRequestClose,
  projectNodeId,
  assets,
  intl,
}) => {
  const { data } = useQuery(GET_PHOTO_PAGE_LAYOUTS);

  const { onReportCreate } = useShowReportTemplateTooltip();

  const selectedItems = get(assets, 'assetsByType.photos', []);
  const { formatMessage } = intl;

  const reportCreateValidationSchema = () =>
    Yup.object().shape({
      title: Yup.string().required(formatMessage(messages.titleRequired)),
      layoutId: Yup.string().required(formatMessage(messages.layoutRequired)),
    });

  const [createReport] = useMutation(CREATE_REPORT, {
    onCompleted: () => {
      CCEvents.logEvent(EventNames.REPORTS.CREATE_REPORT);
    },
    onError: () => {
      toast.error(formatMessage(messages.createError));
    },
  });

  const [createReportNodes] = useMutation(CREATE_REPORT_NODE_BULK, {
    onError: () => {
      toast.error(messages.addItemError);
    },
  });

  const [createSection] = useMutation(CREATE_REPORT_NODE, {
    onError: () => {
      toast.error(messages.sectionCreateError);
    },
  });

  const handleMoveItems = async (newSectionId, newReportId) => {
    const photoDatabaseIds = selectedItems.map(databaseIdFrom);
    const imageIds = photoDatabaseIds.map(imageNodeForId);

    createReportNodes({
      variables: {
        input: {
          id: newReportId,
          position: 1,
          imageIds,
          parentId: newSectionId,
        },
      },
    }).then(() => {
      Turbo.visit(`/reports/${newReportId}/edit/${newSectionId}`);
    });
  };

  const handleCreateSection = async (reportId) => {
    await createSection({
      variables: {
        input: {
          ...NEW_SECTION_DEFAULTS,
          reportId,
          data: {
            assetSection: {
              summary: '',
            },
          },
        },
      },
    }).then((result) => {
      const newSection = get(
        result,
        'data.reportNodeCreate.reportNode' || null,
      );
      if (newSection) {
        handleMoveItems(newSection.id, reportId);
      }
    });
  };

  const handleCreateSubmit = (values) => {
    const { title, layoutId } = values;
    createReport({
      variables: {
        input: {
          projectId: projectNodeId,
          title,
          assetSectionLayoutId: layoutId,
        },
      },
    }).then((result) => {
      const newReport = get(result, 'data.reportCreate.report' || null);
      if (newReport) {
        if (assets) {
          handleCreateSection(newReport.id);
        } else {
          Turbo.visit(`/reports/${newReport.id}/edit`);
          onReportCreate();
        }
      }
    });
  };

  const initialValues = {
    title: '',
    layoutId: '',
  };

  return (
    <Modal
      ariaHideApp={false}
      contentLabel="Create Report"
      isOpen
      overlayClassName="modal"
      className="modal-content-wrap is-fullscreen create-report"
    >
      <h3 className="modal-title">{formatMessage(messages.modalTitle)}</h3>
      {isClosable ? (
        <ActionLink className="modal-close-button" onClick={onRequestClose}>
          <i className="mdi mdi-close" />
        </ActionLink>
      ) : null}
      <div>
        <Formik
          onSubmit={handleCreateSubmit}
          initialValues={initialValues}
          validationSchema={reportCreateValidationSchema}
        >
          {(formikProps) => (
            <CreateForm {...formikProps} layouts={getLayouts(data)} />
          )}
        </Formik>
      </div>
    </Modal>
  );
};

CreateReport.propTypes = {
  onRequestClose: PropTypes.func,
  isOpen: PropTypes.bool,
  isClosable: PropTypes.bool,
  projectNodeId: PropTypes.string,
  assets: PropTypes.object,
  intl: PropTypes.object,
};

CreateReport.defaultProps = {
  isOpen: false,
  onRequestClose: () => {},
  isClosable: true,
};

export default injectIntl(CreateReport);
